import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { imageKit } from '@meitu/wasm-lib';
import { checkPointInStage, convertToLayerPoint, getFramework, useIdentity, useScale } from '@/framework';
import { IPoint } from '@/version-types';
import { IntelligentColorPlugin } from '../controller';
import { useIntelligentColorState } from '../states/intelligent-color-state';
import { SMART_MASK_PEN_EVENTS } from '../types';
import daubPenCursor from './assets/daub-pen.svg';
import './smart-mask-pen.less';

export const SmartMaskPen = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const stageIndex = framework.stage.getActiveStageIndex();
    const scale = useScale();
    const isProtectPen = useIntelligentColorState()((state) => state.isProtectPen);
    const penSize = useIntelligentColorState()((state) => state.intelligentPenSize);
    const intelligentPreData = useIntelligentColorState()((state) => state.intelligentPreData);
    const selectedData = useIntelligentColorState()((state) => state.selectedInfo);
    const smartMaskType = useIntelligentColorState()((state) => state.smartPenType);

    const setIntelligentPreData = useIntelligentColorState()((state) => state.setIntelligentPreData);

    const intelligentController = framework.getInstance(IntelligentColorPlugin);
    const { color } = selectedData;
    const isSelectAll = !color;
    // 绘制时候的画笔大小
    const drawPenSize = penSize / scale;

    const [mousePosition, setMousePosition] = useState<IPoint>({ x: -10000, y: 0 });
    const [inStage, setInStage] = useState<boolean>(false);
    const [isInMaskPen, setIsInMaskPen] = useState<boolean>(false);

    useEffect(() => {
        const updatePreviewList = async (e: MouseEvent) => {
            if (!isInMaskPen) return;

            framework.operate.globalMask.show({ loading: true });

            try {
                const old = cloneDeep(intelligentPreData);
                const maskCode = await intelligentController.getMaskData();

                if (maskCode) {
                    old.maskCode = maskCode;
                }

                if (isSelectAll) {
                    await intelligentController.renderAllIntelligentPreview();
                } else {
                    await intelligentController.renderIntelligentPreview(color);
                }

                intelligentController.updateLayer();
                setIntelligentPreData(cloneDeep(old));
                framework.events.emit(SMART_MASK_PEN_EVENTS.drawEnd, smartMaskType);
            } catch (error) {
                console.error('error:', error);
            } finally {
                setIsInMaskPen(false);
                framework.operate.globalMask.hide();
            }
        };

        const glCanvas = imageKit.getGLCanvas();

        glCanvas?.addEventListener('mousedown', () => {
            setIsInMaskPen(true);
        });
        document?.documentElement.addEventListener('mouseup', updatePreviewList);

        return () => {
            glCanvas?.removeEventListener('mousedown', () => {
                setIsInMaskPen(false);
            });
            document?.documentElement.removeEventListener('mouseup', updatePreviewList);
        };
    }, [isSelectAll, color, smartMaskType, intelligentPreData, isInMaskPen]);

    useEffect(() => {
        function init() {
            const glCanvas = imageKit.getGLCanvas();

            if (glCanvas) {
                document.getElementById('J_smartMaskPen')?.appendChild(glCanvas);
            }

            return () => {
                if (glCanvas) {
                    document.getElementById('GLContainer')?.appendChild(glCanvas);
                }
            };
        }
        const handler = framework.events.onLayerChange(() => {
            // HACK: 这里需要延迟一下，因为图层变化的时候，glCanvas 还没有挂载到 dom 上
            setTimeout(() => {
                init();
            }, 0);
        });

        init();

        return () => {
            framework.events.off(handler);
        };
    }, []);

    useEffect(() => {
        const moveHandle = async (e) => {
            const point = { x: e.x, y: e.y };
            // 设置鼠标位置
            const position = convertToLayerPoint(identity, point, stageIndex);
            const inRect = checkPointInStage(identity, point, stageIndex);

            setMousePosition(position);

            if (!inRect) return setInStage(false);
            if (inRect) {
                setInStage(true);
            }
        };

        document.addEventListener('mousemove', moveHandle);
        return () => {
            document.removeEventListener('mousemove', moveHandle);
        };
    }, []);

    return (
        <div className="m-smart-mask-pen" id="J_smartMaskPen">
            {inStage && isProtectPen ? (
                <div
                    style={{
                        position: 'absolute',
                        width: drawPenSize,
                        height: drawPenSize,
                        transform: `translate(-50%, -50%)`,
                        left: mousePosition.x,
                        top: mousePosition.y,
                    }}
                    className="m-draw-pen"
                >
                    <img src={daubPenCursor} style={{ display: 'block' }} height="100%" width="100%" alt="" />
                </div>
            ) : null}
        </div>
    );
});
